/**
 * A host's status card: presentation state from the heartbeat, the idle-stop
 * countdown, the doctor summary, and the SSH command for the worst case (spec,
 * "Hosts Page"). Start/Stop use the privileged callables; hold/doctor/sync are
 * ordinary instructions.
 *
 * Power sits in the header next to the status chip — Stop when the host is up,
 * Start when it is not — because it is the same question the chip answers, and a
 * control that jumped between the header and the footer as the host flipped state
 * would be somewhere different every time an operator reached for it. The footer
 * carries only the routine actions, which keeps them on one row.
 *
 * The card answers, top to bottom: which machine is this and what is it for; is
 * it answering right now; what state is its checkout in and when will it stop;
 * is it healthy — and only then, how do I reach it by hand. The role's purpose
 * is stated on the card because the two hosts are not interchangeable: an
 * operator checking for changes needs the prepare host and can ignore a release
 * host that is off, and the card should make that obvious without the design
 * doc. Health is loud only when a check fails; the SSH command is folded away
 * because it is the last resort, not the first thing to read — except when a
 * boot is overdue, when the status text sends the operator to it. Copy sits
 * next to the fold so a returning operator can grab the command without opening
 * it. The command itself is per-operator when someone hops through another
 * local account (`formatSshCommand`); everyone else gets the stock gcloud line.
 *
 * Everything time-derived (heartbeat age, boot progress, countdown) is computed
 * from `useNow()` so the card changes as time passes, not only when a document
 * does — a host that has stopped beating must flip to offline on its own.
 */
import { useState, type ReactElement } from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import MonitorHeartOutlined from '@mui/icons-material/MonitorHeartOutlined';
import PauseCircleOutlineRounded from '@mui/icons-material/PauseCircleOutlineRounded';
import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import SyncRounded from '@mui/icons-material/SyncRounded';
import TerminalRounded from '@mui/icons-material/TerminalRounded';
import {
    INSTRUCTION_KIND,
    componentBuildSlots,
    hostInstance,
    narrationFor,
    unprovisionedHostMessage,
    type HostDoc,
    type HostRole,
    type InstructionKind,
} from '@finvari/deployer';
import { useHost, useNow, useRecentInstructions } from '../hooks';
import {
    agoText,
    agoTextCoarse,
    bootOverdue,
    formatDuration,
    hostStatusLine,
    hostView,
    HEARTBEAT_LATE_MS,
    PRESENTATION_COLOR,
    PRESENTATION_LABEL,
    type HostEscalation,
    type HostView,
} from '../lib/hostStatus';
import { formatSshCommand } from '../lib/sshCommand';
import {
    describeActivity,
    inflightRows,
    INFLIGHT_DISABLED_REASON,
    isInflightOrRecent,
    latestByKind,
    resetAwaitingStart,
    type ActivityHost,
    type InstructionRow,
} from '../lib/instructionActivity';
import { createInstruction } from '../lib/createInstruction';
import { dbForRole } from '../firebase';
import { useOperator } from '../auth';
import { ActivityAlert } from './ActivityAlert';
import { FactList, type Fact } from './FactList';
import { machineTypeChoicesFor } from './MachineTypeMenu';
import { StartHostButton } from './StartHostButton';

/**
 * What each machine is for, in the operator's terms. The hosts are asymmetric —
 * one prepares, one deploys, and only the prepare host is involved in answering
 * "is there anything to release?" — and the card is where an operator learns
 * which one they need right now.
 */
const ROLE_PURPOSE: Record<HostRole, string> = {
    prepare:
        'Merges main into the release branch, captures the PR diffs, drafts the announcement, and publishes the tag. Holds GitHub and Claude credentials, never production ones.',
    release:
        'Compiles and deploys components to production once a release is published. Holds the production deploy identity, so it runs only while a release is open.',
};

const ROLE_TITLE: Record<HostRole, string> = {
    prepare: 'Prepare host',
    release: 'Release host',
};

interface HostAction {
    kind: InstructionKind;
    icon: ReactElement;
    color?: 'warning';
    /**
     * Ask before creating the instruction. Set only for Reset checkout: it is the
     * one action on this card that destroys something an engineer may have SSH'd in
     * to do, and it sits one button along from Sync.
     */
    confirm?: boolean;
}

/**
 * The routine actions offered while the host is online, in footer display order.
 *
 * Reset sits next to Sync because it is the answer when Sync reports a dirty tree,
 * and it is the one action here that destroys something (the working tree), so it
 * carries the warning colour that Stop does.
 */
const ONLINE_ACTIONS: HostAction[] = [
    { kind: INSTRUCTION_KIND.hostDoctor, icon: <MonitorHeartOutlined /> },
    { kind: INSTRUCTION_KIND.hostSync, icon: <SyncRounded /> },
    { kind: INSTRUCTION_KIND.hostClean, icon: <RestartAltRounded />, color: 'warning', confirm: true },
    { kind: INSTRUCTION_KIND.hostHold, icon: <PauseCircleOutlineRounded /> },
];

/**
 * Stop belongs with the status chip in the header, not with the routine actions:
 * it is the counterpart of Start, and putting both where the card says whether the
 * host is up keeps "is it running" and "change that" in one place instead of
 * moving the control between the header and the footer as the host flips state.
 */
const STOP_ACTION: HostAction = { kind: INSTRUCTION_KIND.hostStopVm, icon: <PowerSettingsNewRounded />, color: 'warning' };

/** Every action whose progress the card reports, wherever it is rendered. */
const ACTIVITY_ACTIONS: HostAction[] = [...ONLINE_ACTIONS, STOP_ACTION];

/** Short sha for the checkout line. */
const SHA_CHARS = 8;

/**
 * Where this page sends an operator whose host is misbehaving. The SSH command is
 * on this card, so the escalation can point at it directly.
 */
const CARD_ESCALATION: HostEscalation = {
    remediation: 'check the VM in the Cloud Console or SSH in with the command below',
};

/** The label/value facts the card states about the host, by what is known. */
function hostFacts(view: HostView, doc: HostDoc | undefined, now: number, doctorRunning: boolean): Fact[] {
    const facts: Fact[] = [];
    const online = view.presentation === 'online';

    if (online) {
        // How long it has been up is the fact worth stating; that it is answering is
        // already the chip's job. The heartbeat age joins it only when a beat is
        // overdue (HEARTBEAT_LATE_MS), which is the one case where watching the
        // seconds tells the operator something — the host is about to drop offline.
        const up = doc?.uptimeSeconds === undefined ? undefined : formatDuration(doc.uptimeSeconds * 1000);
        const beatAge = view.heartbeatAgeMs;
        const beat = beatAge !== undefined && beatAge > HEARTBEAT_LATE_MS ? `last heartbeat ${agoText(beatAge)}` : undefined;
        const value = [up, beat].filter(Boolean).join(' · ');
        if (value) facts.push({ label: 'Uptime', value });
    }

    // What the VM actually is, which nothing outside it knows any more: the release
    // host's machine type is an operator's choice once a stockout has forced one,
    // and Pulumi leaves it alone afterwards. The build limit rides along because it
    // is the consequence of the shape the operator is looking at — switching to a
    // smaller machine to get a release out costs parallelism, and this is where
    // that shows.
    if (doc?.machine) {
        const { machineType, vcpus, memoryGib } = doc.machine;
        const slots = machineTypeChoicesFor(doc.role)
            ? `up to ${componentBuildSlots(memoryGib)} components at once`
            : undefined;
        facts.push({
            label: 'Machine',
            value: [machineType, `${vcpus} vCPU`, `${memoryGib} GiB`, slots].filter(Boolean).join(' · '),
        });
    }

    if (doc?.checkoutBranch) {
        facts.push({
            label: 'Checkout',
            value: (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>
                        {doc.checkoutBranch} @ {doc.checkoutHead?.slice(0, SHA_CHARS)}
                    </Box>
                    {/*
                      * A dirty tree blocks sync and the prepare guards, so it is a
                      * warning the operator has to act on, not a detail. Reset checkout
                      * in the footer is the action it calls for.
                      */}
                    {doc.checkoutDirty && <Chip size="small" color="warning" variant="outlined" label="uncommitted changes" />}
                    {/*
                      * The controller is built from the checkout when it starts, and
                      * `host.sync` moves the checkout under it afterwards — so the sha
                      * above is where the checkout is, not necessarily what is running.
                      * Stated beside it because every step and instruction on this host
                      * uses the older code until it is restarted, and the card is
                      * otherwise indistinguishable from a host that is fully current.
                      */}
                    {doc.controllerSha && doc.checkoutHead && doc.controllerSha !== doc.checkoutHead && (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`controller built from ${doc.controllerSha.slice(0, SHA_CHARS)}`}
                        />
                    )}
                </Stack>
            ),
        });
    }

    // What the boot did with a reset request, for as long as this boot lasts. The
    // instruction that asked for it succeeded on a host that no longer exists, so its
    // log stops at "stopping the host now" and this is the only place the other half
    // of the action is reported.
    if (doc?.checkoutReset) {
        const reset = doc.checkoutReset;
        facts.push({
            label: 'Checkout reset',
            value: (
                <Box component="span" sx={{ color: reset.ok ? undefined : 'error.main' }}>
                    {reset.ok
                        ? `Emptied at boot — ${reset.removed} path${reset.removed === 1 ? '' : 's'} removed`
                        : 'Failed at boot'}
                    {reset.requestedBy ? ` · asked for by ${reset.requestedBy}` : ''}
                    {reset.detail ? ` · ${reset.detail}` : ''}
                </Box>
            ),
        });
    }

    if (online) {
        if (view.held) {
            facts.push({ label: 'Idle stop', value: 'Held — the idle timer is suspended until the hold is cleared.' });
        } else if (view.idleStopInMs !== undefined) {
            facts.push({ label: 'Idle stop', value: `in ${formatDuration(view.idleStopInMs)} unless something runs` });
        }
    }

    if (doctorRunning) {
        facts.push({
            label: 'Health',
            value: (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                    ...
                </Box>
            ),
        });
    } else if (doc?.doctor && doc.doctor.length > 0) {
        const failing = doc.doctor.filter((c) => !c.ok).length;
        const total = doc.doctor.length;
        const when = doc.doctorAt?.toMillis ? ` · checked ${agoTextCoarse(now - doc.doctorAt.toMillis())}` : '';
        facts.push({
            label: 'Health',
            value: (
                <Box component="span" sx={{ color: failing > 0 ? 'error.main' : undefined }}>
                    {failing > 0 ? `${failing} of ${total} checks failing` : `${total} of ${total} checks passing`}
                    {when}
                </Box>
            ),
        });
    }

    return facts;
}

export function HostCard({ role }: { role: HostRole }) {
    const operator = useOperator();
    const { value: doc, loading } = useHost(role);
    const now = useNow();
    const view = hostView(doc, now, loading);
    const vm = hostInstance(role);
    const recent = useRecentInstructions(role).value;
    const latest = latestByKind(recent);
    const activityHost: ActivityHost = { role, view, expectedBootSeconds: vm?.expectedBootSeconds };

    /** Which management instruction's create is in the air right now. */
    const [sending, setSending] = useState<InstructionKind | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [confirming, setConfirming] = useState<InstructionKind | undefined>(undefined);
    const [sshOpen, setSshOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const online = view.presentation === 'online';
    const starting = view.presentation === 'starting';
    const stopping = view.presentation === 'stopping';
    const overdue = !!vm && bootOverdue(view, vm.expectedBootSeconds);
    // Both the overdue-boot and the failed-stop text tell the operator to SSH in, so
    // the command is unfolded for them rather than hidden behind the toggle it just
    // pointed at.
    const showSsh = sshOpen || overdue || !!view.stopOverdue;
    const sshCommand = vm ? formatSshCommand(vm, operator.email) : undefined;

    const send = async (kind: InstructionKind) => {
        setSending(kind);
        setError(undefined);
        const result = await createInstruction(dbForRole(role), role, kind, {}, operator);
        setSending(undefined);
        setConfirming(undefined);
        if (!result.ok) setError(result.error);
    };

    const copySsh = async () => {
        if (!sshCommand) return;
        await navigator.clipboard.writeText(sshCommand);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    // While a new health check is running, the previous run's results come down.
    // A red line from the last run beside a spinner for this one reads as two
    // facts when it is one stale one, and the replacement is seconds away. Only
    // `running` counts — the host has claimed it and is producing the new answer;
    // a check that is merely queued might never be picked up, and hiding the last
    // real result behind it would leave the card saying nothing indefinitely.
    const doctorRunning = latest.get(INSTRUCTION_KIND.hostDoctor)?.status === 'running';
    const failedChecks = doctorRunning ? [] : (doc?.doctor ?? []).filter((c) => !c.ok);
    const status = hostStatusLine(view, vm, CARD_ESCALATION);
    const facts = hostFacts(view, doc, now, doctorRunning);
    // Reset checkout stops the host and the next start performs it, so an operator
    // looking at an offline card has done half an action and cannot tell: the
    // instruction says "Succeeded" and the host says "Offline", which is what a
    // Shutdown looks like too.
    // Not while starting: that boot is performing the reset, and telling the operator
    // to start a host that is starting would read as nothing having happened.
    const awaitingReset = !online && !starting && resetAwaitingStart(doc, latest.get(INSTRUCTION_KIND.hostClean));
    // Named in the confirmation, because "anything running is cancelled" is abstract
    // until the operator reads that it is the functions deploy they started.
    const inflight = inflightRows(recent);
    const recentActivity = ACTIVITY_ACTIONS.map((a) => latest.get(a.kind)).filter(
        (row): row is NonNullable<typeof row> => !!row && isInflightOrRecent(row, now),
    );
    const chipLabel = view.presentation === 'unknown' && !vm ? 'Not registered' : PRESENTATION_LABEL[view.presentation];

    /** One management action, identical whether it sits in the header or the footer. */
    const actionButton = ({ kind, icon, color, confirm }: HostAction) => {
        const row = latest.get(kind);
        const reason = row && describeActivity(row, activityHost, now).inflight ? INFLIGHT_DISABLED_REASON : undefined;
        return (
            <Tooltip key={kind} title={reason ?? ''}>
                <span>
                    <Button
                        size="small"
                        color={color}
                        onClick={() => (confirm ? setConfirming(kind) : void send(kind))}
                        disabled={sending !== undefined || !!reason}
                        startIcon={sending === kind ? <CircularProgress size={14} color="inherit" /> : icon}
                    >
                        {narrationFor(kind).title}
                    </Button>
                </span>
            </Tooltip>
        );
    };

    // No control until the first snapshot says which one is right. Offering Start
    // here is what made a running host briefly look stopped, and the worse version
    // of that bug is a slow connection holding the wrong button on screen long
    // enough to be clicked.
    //
    // A host mid-boot gets neither control either: Start is done and Stop would
    // race the boot it would be cancelling, so the footer's "waiting for the first
    // heartbeat" is the whole story until it lands.
    //
    // A host mid-halt gets neither for the mirror-image reason: Stop is done, and
    // Start would race a shutdown that is already under way — Compute would either
    // refuse it or bring back a VM that is still powering off. If the halt never
    // takes, the card returns to online past the grace window and Shutdown comes
    // back with it, which is the retry.
    const powerAction =
        loading || stopping || (starting && !overdue) ? undefined : online ? (
            actionButton(STOP_ACTION)
        ) : (
            <StartHostButton
                role={role}
                label={overdue ? 'Start again' : 'Start'}
                size="small"
                currentMachineType={doc?.machine?.machineType}
            />
        );

    return (
        <Card variant="outlined" sx={{ minWidth: 320, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
                <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6">{ROLE_TITLE[role]}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {ROLE_PURPOSE[role]}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        {powerAction}
                        <Chip
                                size="small"
                                variant="outlined"
                                color={PRESENTATION_COLOR[view.presentation]}
                                icon={<FiberManualRecord sx={{ fontSize: 10 }} />}
                                label={chipLabel}
                            />
                        </Stack>
                    </Stack>

                    {status && (
                        <Typography variant="body2" color={status.color}>
                            {status.text}
                        </Typography>
                    )}

                    {awaitingReset && (
                        <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                            <AlertTitle sx={{ fontSize: '0.875rem' }}>Checkout reset is waiting for a start</AlertTitle>
                            <Typography variant="body2">
                                A checkout reset stops the host and is performed by the next boot. Start it: the working
                                tree is emptied before anything is fetched, built or started, and this card reports what
                                it did. The request is on the host&apos;s disk, so it keeps until then.
                            </Typography>
                        </Alert>
                    )}

                    {facts.length > 0 && <FactList facts={facts} />}

                    {failedChecks.length > 0 && (
                        <Stack spacing={1}>
                            {/*
                              * Each failure carries the command's first error line and the
                              * remediation, which is the whole point of running the real
                              * commands: naming the check only ("dvc status failing") sends
                              * the operator over SSH to re-run it and find out what this
                              * already knows. The run time is on the Health line above — a
                              * red line from this morning's check is not evidence about now.
                              */}
                            {failedChecks.map((c) => (
                                <Alert key={c.name} severity="error" variant="outlined" sx={{ py: 0.5 }}>
                                    <AlertTitle sx={{ fontSize: '0.875rem', mb: c.detail ? 0.5 : 0 }}>{c.name}</AlertTitle>
                                    {c.detail && (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                            {c.detail}
                                        </Typography>
                                    )}
                                </Alert>
                            ))}
                        </Stack>
                    )}

                    {/*
                      * An unregistered VM is unfinished configuration, not a failure of the
                      * machine — a warning with the fix, not an error.
                      */}
                    {!vm && (
                        <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                            <AlertTitle sx={{ fontSize: '0.875rem' }}>VM not registered</AlertTitle>
                            <Typography variant="body2">{unprovisionedHostMessage(role)}</Typography>
                        </Alert>
                    )}

                    {recentActivity.length > 0 && (
                        <Stack spacing={0.75}>
                            {recentActivity.map((row) => (
                                <ActivityAlert
                                    key={row.id}
                                    host={role}
                                    row={row}
                                    line={describeActivity(row, activityHost, now)}
                                    label={narrationFor(row.kind).title}
                                    variant="inline"
                                />
                            ))}
                        </Stack>
                    )}

                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}

                    {sshCommand && (
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Button
                                    size="small"
                                    color="inherit"
                                    startIcon={<TerminalRounded />}
                                    endIcon={showSsh ? <ExpandLessRounded /> : <ExpandMoreRounded />}
                                    onClick={() => setSshOpen(!showSsh)}
                                    sx={{ color: 'text.secondary', textTransform: 'none', px: 0.5 }}
                                >
                                    SSH access
                                </Button>
                                <CopySshButton copied={copied} onCopy={() => void copySsh()} />
                            </Stack>
                            <Collapse in={showSsh}>
                                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all', lineHeight: 1.6 }}
                                        >
                                            {sshCommand}
                                        </Typography>
                                        <CopySshButton copied={copied} onCopy={() => void copySsh()} />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                        The last resort: everything the host does is a shell command you can run by hand.
                                    </Typography>
                                </Stack>
                            </Collapse>
                        </Box>
                    )}
                </Stack>
            </CardContent>
            {/*
              * Only the routine actions, so they fit one row. An offline host has no
              * footer at all — its one control is Start, in the header.
              */}
            {(online || (starting && !overdue)) && (
                <CardActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                    {online ? (
                        ONLINE_ACTIONS.map(actionButton)
                    ) : (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 0.5 }}>
                            <CircularProgress size={16} />
                            <Typography variant="body2" color="text.secondary">
                                Waiting for the first heartbeat…
                            </Typography>
                        </Stack>
                    )}
                </CardActions>
            )}

            <ResetCheckoutDialog
                open={confirming === INSTRUCTION_KIND.hostClean}
                role={role}
                inflight={inflight}
                sending={sending === INSTRUCTION_KIND.hostClean}
                error={error}
                onCancel={() => setConfirming(undefined)}
                onConfirm={() => void send(INSTRUCTION_KIND.hostClean)}
            />
        </Card>
    );
}

interface ResetCheckoutDialogProps {
    open: boolean;
    role: HostRole;
    /** What the host is running right now, which this will cancel. */
    inflight: InstructionRow[];
    sending: boolean;
    error?: string;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * The confirmation for Reset checkout, and the only place the whole shape of the
 * action is stated.
 *
 * It needs one because the action is three things an operator would not guess from
 * a button beside Sync: it discards work, it cancels whatever the host is doing,
 * and it takes the host down until they start it again. It is also the one control
 * on this card that destroys something a person may have SSH'd in to create, one
 * button along from a Sync that destroys nothing.
 */
function ResetCheckoutDialog({ open, role, inflight, sending, error, onCancel, onConfirm }: ResetCheckoutDialogProps) {
    const narration = narrationFor(INSTRUCTION_KIND.hostClean);
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Reset the {role} host&apos;s checkout?</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText>{narration.what}</DialogContentText>

                    {/*
                      * Numbered because the order is the surprising part: the deletion
                      * happens on the *next* start, not now, and the host does not come
                      * back on its own.
                      */}
                    <DialogContentText component="div">
                        What happens:
                        <ol style={{ margin: '0.5em 0 0', paddingLeft: '1.5em' }}>
                            <li>The host records what is on it — the working tree and every running process — to this
                                instruction&apos;s log, which is the last chance to capture it.</li>
                            <li>Anything running is cancelled.</li>
                            <li>The host shuts down.</li>
                            <li>You press Start. The boot empties the working tree before it fetches, builds or starts
                                anything, and this card reports what it removed.</li>
                            <li>Run Sync, as after any start.</li>
                        </ol>
                    </DialogContentText>

                    {/*
                      * Said plainly because the operator will see it as a surprise
                      * otherwise: the markers are files in the tree, so the release page
                      * asks for compiles and deploys that had already been done.
                      */}
                    <DialogContentText>
                        {narration.reversible} Redeploying a component is harmless — it re-runs the pipeline and
                        changes nothing in production.
                    </DialogContentText>

                    {inflight.length > 0 && (
                        <Alert severity="warning">
                            <AlertTitle sx={{ fontSize: '0.875rem' }}>
                                {inflight.length === 1 ? 'One instruction is' : `${inflight.length} instructions are`} in
                                flight and will be cancelled
                            </AlertTitle>
                            <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {inflight.map((row) => (
                                    <li key={row.id}>
                                        {narrationFor(row.kind).title}
                                        {typeof row.params?.component === 'string' ? ` — ${row.params.component}` : ''}
                                        {` (${row.status})`}
                                    </li>
                                ))}
                            </Stack>
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" color="warning" disabled={sending} onClick={onConfirm}>
                    {inflight.length > 0 ? 'Cancel everything and reset' : 'Reset and shut down'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CopySshButton({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
    return (
        <Tooltip title={copied ? 'Copied' : 'Copy command'}>
            <IconButton size="small" onClick={onCopy} aria-label="Copy SSH command">
                <ContentCopyRounded sx={{ fontSize: 16 }} />
            </IconButton>
        </Tooltip>
    );
}
